import React, { useState, useEffect, useCallback } from 'react';
import { orderApi } from '../../lib/orderApi';
import { goldButton, redButton } from '../PageShell';

const DAYS = [
  { v: 0, l: 'Lunes' }, { v: 1, l: 'Martes' }, { v: 2, l: 'Miércoles' }, { v: 3, l: 'Jueves' },
  { v: 4, l: 'Viernes' }, { v: 5, l: 'Sábado' }, { v: 6, l: 'Domingo' },
];

const cfgLabel = { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#C9A84C', fontWeight: 600 };
const cfgInput = { background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)', color: '#F5F5F0', padding: '9px 11px', fontSize: '13px', outline: 'none', width: '100%' };

const AdminSettings = ({ isOpen, cfg, setCfg, onRefresh }) => {
  const [cfgBusy, setCfgBusy] = useState(false);
  const [cfgMsg, setCfgMsg] = useState('');

  // Route origin (fixed in code) + delivery locations
  const [routeOrigin, setRouteOrigin] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locError, setLocError] = useState('');

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await orderApi.get('/admin/locations');
      setRouteOrigin(data.route_origin || null);
      setLocations(data.locations || []);
      setLocError('');
    } catch (error) {
      console.error('No se pudieron cargar los lugares de entrega:', error);
      setLocError('No se pudieron cargar los lugares de entrega. Intenta actualizar.');
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadLocations();
  }, [isOpen, loadLocations]);

  const saveCfg = async () => {
    if (!cfg) return;
    setCfgBusy(true);
    setCfgMsg('');
    try {
      const { data } = await orderApi.put('/admin/settings', cfg);
      setCfg(data.settings);
      setCfgMsg('✓ Configuración guardada.');
      if (onRefresh) onRefresh();
    } catch (e) {
      setCfgMsg('No se pudo guardar. ' + (e.response?.data?.detail || ''));
    } finally {
      setCfgBusy(false);
    }
  };

  if (!isOpen || !cfg) return null;

  return (
    <div className="mt-5 p-5" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="settings-panel">
      <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>Configuración de pedidos</h3>
      <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
        Define los días y horas de pedidos, la entrega y el límite de órdenes, sin tocar el código.
      </p>

      {/* Orders enabled toggle */}
      <div
        className="mt-4 p-4"
        style={{
          background: cfg.orders_enabled === false ? 'rgba(200,40,43,0.15)' : 'rgba(127,183,126,0.1)',
          border: cfg.orders_enabled === false ? '1px solid rgba(200,40,43,0.4)' : '1px solid rgba(127,183,126,0.3)',
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <label style={{ ...cfgLabel, color: cfg.orders_enabled === false ? '#E0736F' : '#7FB77E' }}>Estado de pedidos</label>
            <p className="font-body mt-1" style={{ fontSize: '14px', color: '#F5F5F0', fontWeight: 600 }}>
              {cfg.orders_enabled === false ? '⏸ Pedidos pausados' : '✓ Pedidos activos'}
            </p>
            <p className="font-body mt-1" style={{ fontSize: '11px', color: '#C2C1B6' }}>
              {cfg.orders_enabled === false
                ? 'Los clientes no pueden hacer pedidos aunque la ventana esté abierta.'
                : 'Los clientes pueden pedir dentro de la ventana horaria configurada.'}
            </p>
          </div>
          <button
            onClick={() => setCfg({ ...cfg, orders_enabled: !cfg.orders_enabled })}
            style={{
              ...(cfg.orders_enabled === false ? goldButton : redButton),
              padding: '10px 20px',
            }}
            data-testid="cfg-orders-toggle"
          >
            {cfg.orders_enabled === false ? 'Activar pedidos' : 'Pausar pedidos'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div>
          <label style={cfgLabel}>Día de apertura</label>
          <select
            value={cfg.order_open_day}
            onChange={(e) => setCfg({ ...cfg, order_open_day: Number(e.target.value) })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-open-day"
          >
            {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </div>
        <div>
          <label style={cfgLabel}>Hora de apertura</label>
          <input
            type="time"
            value={cfg.order_open_time}
            onChange={(e) => setCfg({ ...cfg, order_open_time: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-open-time"
          />
        </div>
        <div>
          <label style={cfgLabel}>Día de cierre</label>
          <select
            value={cfg.order_close_day}
            onChange={(e) => setCfg({ ...cfg, order_close_day: Number(e.target.value) })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-close-day"
          >
            {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </div>
        <div>
          <label style={cfgLabel}>Hora de cierre</label>
          <input
            type="time"
            value={cfg.order_close_time}
            onChange={(e) => setCfg({ ...cfg, order_close_time: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-close-time"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <div>
          <label style={cfgLabel}>Día de entrega</label>
          <select
            value={cfg.delivery_day}
            onChange={(e) => setCfg({ ...cfg, delivery_day: Number(e.target.value) })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-delivery-day"
          >
            {DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </div>
        <div>
          <label style={cfgLabel}>Hora de entrega inicio</label>
          <input
            type="time"
            value={cfg.delivery_start_time}
            onChange={(e) => setCfg({ ...cfg, delivery_start_time: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-delivery-start"
          />
        </div>
        <div>
          <label style={cfgLabel}>Hora de entrega fin</label>
          <input
            type="time"
            value={cfg.delivery_end_time}
            onChange={(e) => setCfg({ ...cfg, delivery_end_time: e.target.value })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-delivery-end"
          />
        </div>
        <div>
          <label style={cfgLabel}>Límite de órdenes por entrega (0 = ilimitado)</label>
          <input
            type="number"
            min="0"
            value={cfg.max_orders}
            onChange={(e) => setCfg({ ...cfg, max_orders: Number(e.target.value) })}
            style={{ ...cfgInput, marginTop: '4px' }}
            data-testid="cfg-max-orders"
          />
        </div>
      </div>
      <div className="flex items-center gap-4 mt-4">
        <button onClick={saveCfg} disabled={cfgBusy} style={{ ...redButton, opacity: cfgBusy ? 0.6 : 1 }} data-testid="settings-save">
          {cfgBusy ? 'Guardando…' : 'Guardar configuración'}
        </button>
        {cfgMsg && (
          <span className="font-body" style={{ fontSize: '12px', color: cfgMsg.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="settings-msg">
            {cfgMsg}
          </span>
        )}
      </div>

      {/* Punto de partida de ruta + lugares de entrega */}
      <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(201,168,76,0.2)' }} data-testid="sedes-section">
        <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>Punto de partida y lugares de entrega</h3>
        <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
          El punto de partida de la ruta del chofer está fijo en el sistema. Toda ruta inicia y termina en la sede de Yamauchi.
        </p>
        {locError && (
          <p className="font-body mt-2" style={{ fontSize: '12px', color: '#E0736F' }} data-testid="locations-error">{locError}</p>
        )}

        {/* Fixed route origin card */}
        <div className="mt-4 p-4" style={{ background: '#0F0F0F', border: '1px solid rgba(201,168,76,0.25)' }} data-testid="route-origin-card">
          <label style={cfgLabel}>Punto de partida de ruta (fijo)</label>
          <div className="flex items-center justify-between gap-3 flex-wrap mt-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-body" style={{ fontSize: '14px', color: '#F5F5F0', fontWeight: 600 }} data-testid="route-origin-name">
                  {routeOrigin?.name || 'Yamauchi'}
                </p>
                <span
                  className="font-body"
                  style={{ fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#0A0A0A', background: '#C9A84C', padding: '2px 8px', fontWeight: 700 }}
                >
                  Punto de partida de ruta
                </span>
              </div>
              {routeOrigin?.lat != null && (
                <p className="font-body mt-1" style={{ fontSize: '11px', color: '#8A8A80' }} data-testid="route-origin-coords">
                  {routeOrigin.lat}, {routeOrigin.lng}
                </p>
              )}
            </div>
            {routeOrigin?.maps_url && (
              <a href={routeOrigin.maps_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#C9A84C', whiteSpace: 'nowrap' }} data-testid="route-origin-maps-link">
                Ver pin en Google Maps →
              </a>
            )}
          </div>
        </div>

        {/* Read-only list of current delivery drop-off locations */}
        <div className="mt-4 p-4" style={{ background: '#0F0F0F', border: '1px solid rgba(201,168,76,0.25)' }}>
          <label style={cfgLabel}>Lugares de entrega actuales ({locations.length})</label>
          <ul className="mt-3 space-y-2" data-testid="locations-list">
            {locations.map((loc) => (
              <li key={loc.id} className="flex items-center justify-between gap-3" data-testid={`location-row-${loc.id}`}>
                <span className="font-body" style={{ fontSize: '13px', color: '#F5F5F0' }}>{loc.name}</span>
                <a href={loc.maps_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#C9A84C', whiteSpace: 'nowrap' }} data-testid={`location-maps-link-${loc.id}`}>
                  Ver pin →
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
